import React, { useMemo } from 'react';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Platform, View, Pressable } from 'react-native';
import { ScrollView } from '@/components/gluestack/scroll-view';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Image, type ImageSource } from 'expo-image';
import { CommunityThemeScope } from '@/components/ui/CommunityThemeScope';
import { communities, CommunityCode, communityCategories, isCommunityCode } from '@/constants/communities';
import { useTranslation } from 'react-i18next';
import { RenderHtml } from '@/components/ui/RenderHtml';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import { createCommunitiesHeader } from '@/components/layout/CommunitiesHeader';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { Box } from '@/components/gluestack/box';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { NotFoundContent } from '@/components/ui/NotFoundContent';
import { WebDocumentTitle } from '@/components/layout/WebDocumentTitle';

type CommunitySiteCode = CommunityCode;

type CommunityInfoSections = {
  about?: string;
  whatYouCanDo?: string;
  rules?: string;
  welcome?: string;
};

type CommunityInfo = {
  description?: string;
  sections?: CommunityInfoSections;
};

type CommunityHeadings = {
  about: string;
  whatYouCanDo: string;
  rules: string;
  welcome: string;
};

// Memoized detail content extracted to module scope
const DetailContent = React.memo(
  ({
    heroSource,
    communityName,
    info,
    headings,
    primaryAction,
  }: {
    heroSource: ImageSource;
    communityName: string;
    info: CommunityInfo;
    headings: CommunityHeadings;
    primaryAction: React.ReactNode;
  }) => (
    <VStack className="mx-auto w-full max-w-5xl gap-6 p-4 md:p-8">
      <VStack className="gap-6">
        <View className="aspect-[1248/702] w-full overflow-hidden rounded-lg">
          <Image source={heroSource} className="h-full w-full" contentFit="cover" />
        </View>
        <HStack className="justify-center">{primaryAction}</HStack>
      </VStack>

      {!!info?.description && (
        <Text bold className="text-typography-700">{info.description}</Text>
      )}

      <VStack className="gap-8">
        {info?.sections?.about && (
          <VStack className="gap-2">
            <Heading size="xl">
              {headings.about.replace('{{communityName}}', communityName)}
            </Heading>
            <RenderHtml noTruncate detectHttpsLinks html={info.sections.about} />
          </VStack>
        )}
        {info?.sections?.whatYouCanDo && (
          <VStack className="gap-2">
            <Heading size="xl">
              {headings.whatYouCanDo.replace('{{communityName}}', communityName)}
            </Heading>
            <RenderHtml noTruncate detectHttpsLinks html={info.sections.whatYouCanDo} />
          </VStack>
        )}
        {info?.sections?.rules && (
          <VStack className="gap-2">
            <Heading size="xl">{headings.rules}</Heading>
            <RenderHtml noTruncate detectHttpsLinks html={info.sections.rules} />
          </VStack>
        )}
        {info?.sections?.welcome && (
          <VStack className="gap-2">
            <Heading size="xl">
              {headings.welcome.replace('{{communityName}}', communityName)}
            </Heading>
            <RenderHtml noTruncate detectHttpsLinks html={info.sections.welcome} />
          </VStack>
        )}
      </VStack>
    </VStack>
  )
);

// Large-screen community tile extracted and memoized
const LargeScreenCommunityTile = React.memo(
  ({ c, selected, onPress }: { c: CommunitySiteCode; selected: boolean; onPress: () => void }) => {
    const scale = useSharedValue(1);
    const animatedStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }));
    const handlePressIn = () => { scale.value = withTiming(0.98, { duration: 100 }); };
    const handlePressOut = () => { scale.value = withTiming(1, { duration: 120 }); };
    const containerClassName = selected ? 'border-2 border-primary-500 p-0.5 bg-background-0' : '';
    return (
      <Pressable onPress={onPress} onPressIn={handlePressIn} onPressOut={handlePressOut} className="w-full">
        <Animated.View style={[{ width: '100%' }, animatedStyle]}>
          <View className={`aspect-[660/420] w-full overflow-hidden rounded-xl ${containerClassName}`}>
            <View className="h-full w-full overflow-hidden rounded-lg">
              <Image source={communities[c].tileBanner as ImageSource} className="h-full w-full" contentFit="cover" />
            </View>
          </View>
        </Animated.View>
      </Pressable>
    );
  }
);

export default function CommunityDetail() {
  const { community } = useLocalSearchParams<{ community?: CommunityCode }>();
  const communityCode = isCommunityCode(community) ? community : undefined;
  const { t } = useTranslation(['default', 'domain']);
  const openWebPage = useOpenWebPage();
  const router = useRouter();
  const isLargeScreen = useIsLargeScreen();
  const selectedCommunity = communityCode ? communities[communityCode] : undefined;

  const info = communityCode
    ? t(`CommunityInfo.communities.${communityCode}`, {
        ns: 'domain',
        returnObjects: true,
      }) as CommunityInfo
    : {};
  const headings = t('CommunityInfo.headings', { ns: 'domain', returnObjects: true }) as CommunityHeadings;

  const primaryAction = useMemo(() => {
    if (!communityCode || !selectedCommunity) return null;

    if (Platform.OS === 'web') {
      return (
        <Button onPress={() => openWebPage(`https://app.${selectedCommunity.domain}`)}>
          <ButtonText>{t('communities.visitSite', { communityName: selectedCommunity.name })}</ButtonText>
        </Button>
      );
    }
    return (
      <HStack className="gap-3">
        <Button onPress={() => {
          router.push({ pathname: '/communities/[community]/sign-in', params: { community: communityCode } });
        }}>
          <ButtonText>{t('auth.signIn.title')}</ButtonText>
        </Button>
        <Button variant="outline" onPress={() => {
          router.push({ pathname: '/communities/[community]/sign-up', params: { community: communityCode } });
        }}>
          <ButtonText>{t('auth.createAccount')}</ButtonText>
        </Button>
      </HStack>
    );
  }, [communityCode, openWebPage, router, selectedCommunity, t]);

  if (!communityCode || !selectedCommunity) {
    return (
      <>
        <WebDocumentTitle title="Community" />
        <Stack.Screen options={{ header: createCommunitiesHeader({ title: 'Community', backHref: '/communities' }) }} />
        <NotFoundContent />
      </>
    );
  }

  // Split-screen layout on large screens: left list, right details
  if (isLargeScreen) {
    return (
      <>
        <WebDocumentTitle appName={selectedCommunity.name} />
        <Stack.Screen options={{ header: createCommunitiesHeader({ title: selectedCommunity?.name || '', backHref: '/communities', rightIconMode: false }) }} />
        <CommunityThemeScope community={communityCode}>
          <HStack className="mx-auto w-full max-w-7xl flex-1">
            {/* Left panel: communities list */}
            <VStack className="w-2/5 bg-background-0">
              <ScrollView>
                <VStack className="mx-auto w-full max-w-3xl gap-8 p-4 md:p-8">
                  {communityCategories.map((category, idx) => (
                    <VStack
                      key={category.key}
                      className={`gap-4`}
                    >
                      <Heading size="xl">
                        {t(`CommunityCategories.${category.key}.Name`, { ns: 'domain' })}
                      </Heading>
                      <Box className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {category.communities.filter(isCommunityCode).map((c) => (
                          <VStack key={c} className="gap-1">
                            <LargeScreenCommunityTile
                              c={c}
                              selected={c === communityCode}
                              onPress={() => {
                                router.replace({ pathname: '/communities/[community]', params: { community: c } });
                              }}
                            />
                          </VStack>
                        ))}
                      </Box>
                    </VStack>
                  ))}
                </VStack>
              </ScrollView>
            </VStack>

            {/* Right panel: selected community details */}
            <VStack className="flex-1">
              <ScrollView className="">
                <DetailContent
                  heroSource={communities[communityCode].heroBanner as ImageSource}
                  communityName={selectedCommunity.name}
                  info={info}
                  headings={headings}
                  primaryAction={primaryAction}
                />
              </ScrollView>
            </VStack>
          </HStack>
        </CommunityThemeScope>
      </>
    );
  }

  // Small screens: original single-pane detail view
  return (
    <>
      <WebDocumentTitle appName={selectedCommunity.name} />
      <Stack.Screen options={{ header: createCommunitiesHeader({ title: selectedCommunity?.name || 'Community', backHref: '/communities', headerClassName: 'bg-neutral-800' }) }} />
      <CommunityThemeScope community={communityCode}>
        <ScrollView className="bg-background-0">
          <DetailContent
            heroSource={communities[communityCode].heroBanner as ImageSource}
            communityName={selectedCommunity.name}
            info={info}
            headings={headings}
            primaryAction={primaryAction}
          />
        </ScrollView>
      </CommunityThemeScope>
    </>
  );
}


